import type { Upload as S3Upload } from '@aws-sdk/lib-storage';
import { Upload, useUploadFilesContext } from 'components/UploadFilesContext';
import { useRef } from 'react';
import { uuidv4 } from 'utils/helpers';
import { useLabs } from 'utils/queryHooks/useLabs';
import { deleteAwsTemporaryCredentials, getAwsTemporaryCredentials, getS3UploadObject } from './S3Utils';
export interface UploadItem {
  id: string;
  file: File;
  managedUpload?: S3Upload;
}

export const useS3Upload = (labId: string, studyId: string) => {
  const { setFileUploads, setShowUploadWidget } = useUploadFilesContext();
  const uploadsQueueRef = useRef<Array<{ id: string; fileToUpload: File }>>([]);
  const isBusy = useRef(false);

  const { data: labs, isLoading: isLoadingLabs } = useLabs();
  const customerBucketName = labs?.find((lab) => lab.id === labId)?.customerBucketName;

  const getCredentialsAndStartUpload = async (file: File) => {
    if (isLoadingLabs) {
      throw new Error('Lab storage metadata is still loading. Please try again in a moment.');
    }

    const uploadId = uuidv4();
    const optimisticItem: Upload = {
      id: uploadId,
      file,
      managedUpload: undefined,
    };

    uploadsQueueRef.current.push({ id: uploadId, fileToUpload: file });
    setFileUploads((prevFileUploads) => ({
      ...prevFileUploads,
      [uploadId]: optimisticItem,
    }));
    setShowUploadWidget(true);

    if (isBusy.current) {
      return;
    }

    isBusy.current = true;
    const creds = await getAwsTemporaryCredentials(labId);

    const uploadPromises = [];

    while (uploadsQueueRef.current.length) {
      const { id, fileToUpload } = uploadsQueueRef.current.shift();

      const upload = getS3UploadObject(creds.Credentials, labId, studyId, fileToUpload, customerBucketName);

      const item: Upload = {
        id,
        file: fileToUpload,
        managedUpload: upload,
        progress: 0,
        error: undefined,
      };

      upload.on('httpUploadProgress', (progress) => {
        const uploadedBytes = progress.loaded;
        const totalBytes = progress.total ?? fileToUpload.size;
        const percentage = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
        setFileUploads((prevFileUploads) => ({ ...prevFileUploads, [id]: { ...item, progress: percentage } }));
      });

      const promise = upload
        .done()
        .then((data) => {
          setFileUploads((prevFileUploads) => ({ ...prevFileUploads, [id]: { ...item, progress: 100 } }));
          return data;
        })
        .catch((error: Error) => {
          console.error(error.name, error.message);
          setFileUploads((prevFileUploads) => ({ ...prevFileUploads, [id]: { ...item, error } }));
          if (error.name !== 'AbortError') {
            throw error;
          }
        });

      uploadPromises.push(promise);
    }

    try {
      await Promise.allSettled(uploadPromises);
    } finally {
      isBusy.current = false;
      await deleteAwsTemporaryCredentials(creds.Credentials);
    }
  };

  return { getCredentialsAndStartUpload, isLabMetadataLoading: isLoadingLabs };
};
